import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Message } from '../models/domain';
import { Observable } from 'rxjs';
import { Course, CourseList, CourseReport, CourseSlide, CourseTest, CourseTestQuestionnaire, EmployeeAnswer, EmployeeCourseAllocation, EmployeeReport, EmployeeTest, Question, QuestionCategory } from '../models/course';

@Injectable({
  providedIn: 'root'
})
export class CourseService {

  constructor(private httpClient: HttpClient) { }

  // Course APIs
  public createCourse(Course: Course): Observable<Message> {
    return this.httpClient.post<Message>(`/api/course/v1`, Course);
  }

  public getCourse(id: number): Observable<Course> {
    return this.httpClient.get<Course>(`/api/course/v1/${id}`);
  }

  public getCourses(disabled?: boolean): Observable<Course[]> {
    return this.httpClient.get<Course[]>(`/api/course/v1`, { params: { ...(disabled != null && { disabled }) } });
  }

  public getCourseList(employeeId:number,disabled?: boolean): Observable<CourseList[]> {
    return this.httpClient.get<CourseList[]>(`/api/course/v1/course-employees`, { params: { ...(employeeId && { employeeId }),...(disabled != null && { disabled }) } });
  }

  public updateCourse(id: number, Course: Course): Observable<Message> {
    return this.httpClient.put<Message>(`/api/course/v1/${id}`, Course);
  }

  public deleteCourse(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/course/v1/${id}`);
  }

  public toggleCourseStatus(course: Course): Observable<Message> {
    return this.httpClient.put<Message>(`/api/course/v1/${course.id}`, course);
  }

  public getCourseStatus(): Observable<string[]> {
    return this.httpClient.get<string[]>(`/api/course/v1/status`);
  }

  //Course Slide
  public createCourseSlide(courseSlide: CourseSlide): Observable<Message> {
    return this.httpClient.post<Message>(`/api/course/v1/course-slide`, courseSlide);
  }

  public getCourseSlides(courseId: number, disabled?: boolean): Observable<CourseSlide[]> {
    return this.httpClient.get<CourseSlide[]>(`/api/course/v1/course-slide`, {
      params: {
        ...(courseId && { courseId }),
        ...(disabled !== undefined ? { disabled } : {})
      }
    });
  }

  public getCourseSlide(id: number): Observable<CourseSlide> {
    return this.httpClient.get<CourseSlide>(`/api/course/v1/course-slide/${id}`);
  }

  public updateCourseSlide(id: number, courseSlide: CourseSlide): Observable<Message> {
    return this.httpClient.put<Message>(`/api/course/v1/course-slide/${id}`, courseSlide);
  }

  public deleteCourseSlide(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/course/v1/course-slide/${id}`);
  }

  public upsertCourseSlideImage(courseSlideId: number, file?: FormData): Observable<Message> {
    return this.httpClient.post<Message>(`/api/course/v1/course-slide/upload?courseSlideId=${courseSlideId}`, file);
  }

  // QuestionCategory APIs
  public createQuestionCategory(questionCategory: QuestionCategory): Observable<Message> {
    return this.httpClient.post<Message>(`/api/course/v1/question-category`, questionCategory);
  }

  public getQuestionCategory(id: number): Observable<QuestionCategory> {
    return this.httpClient.get<QuestionCategory>(`/api/course/v1/question-category/${id}`);
  }

  public getQuestionCategories(disabled?: boolean): Observable<QuestionCategory[]> {
    return this.httpClient.get<QuestionCategory[]>(`/api/course/v1/question-category`, {
      params: { ...(disabled != null && { disabled }) }
    });
  }

  public updateQuestionCategory(id: number, questionCategory: QuestionCategory): Observable<Message> {
    return this.httpClient.put<Message>(`/api/course/v1/question-category/${id}`, questionCategory);
  }

  public deleteQuestionCategory(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/course/v1/question-category/${id}`);
  }

  public toggleQuestionCategoryStatus(questionCategory: QuestionCategory): Observable<Message> {
    return this.httpClient.put<Message>(`/api/course/v1/question-category/${questionCategory.id}`, questionCategory);
  }

  public getApprovalStatus(): Observable<string[]> {
    return this.httpClient.get<string[]>(`/api/course/v1/question-category/approval-status`);
  }

  // CourseTest APIs
  public createCourseTest(courseTest: CourseTest): Observable<Message> {
    return this.httpClient.post<Message>(`/api/course/v1/course-test`, courseTest);
  }

  public getCourseTest(id: number): Observable<CourseTest> {
    return this.httpClient.get<CourseTest>(`/api/course/v1/course-test/${id}`);
  }

  public getCourseTests(courseId: number, status?: string, disabled?: boolean): Observable<CourseTest[]> {
    const params: any = { courseId };
    if (status !== undefined && status !== null) {
      params.status = status;
    }
    if (disabled !== undefined && disabled !== null) {
      params.disabled = disabled;
    }
    return this.httpClient.get<CourseTest[]>('/api/course/v1/course-test', { params });
  }

  public updateCourseTest(id: number, courseTest: CourseTest): Observable<Message> {
    return this.httpClient.put<Message>(`/api/course/v1/course-test/${id}`, courseTest);
  }

  public deleteCourseTest(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/course/v1/course-test/${id}`);
  }

  public toggleCourseTestStatus(courseTest: CourseTest): Observable<Message> {
    return this.httpClient.put<Message>(`/api/course/v1/course-test/${courseTest.id}`, courseTest);
  }

  public getQuestionTypes(): Observable<string[]> {
    return this.httpClient.get<string[]>(`/api/course/v1/course-question/question-type`);
  }

  // Course Test Questionnaire APIs
  public createCourseTestQuestionnaire(courseTestQuestionnaire: CourseTestQuestionnaire): Observable<Message> {
    return this.httpClient.post<Message>(`/api/course/v1/course-test-questionnaire`, courseTestQuestionnaire);
  }

  public getCourseTestQuestionnaires(courseTestId: number, disabled?: boolean): Observable<CourseTestQuestionnaire[]> {
    return this.httpClient.get<CourseTestQuestionnaire[]>(`/api/course/v1/course-test-questionnaire`, {
      params: { ...(courseTestId && { courseTestId }), ...(disabled && { disabled }) }
    });
  }

  public getCourseTestQuestionnaire(id: number): Observable<CourseTestQuestionnaire> {
    return this.httpClient.get<CourseTestQuestionnaire>(`/api/course/v1/course-test-questionnaire/${id}`);
  }

  public updateCourseTestQuestionnaire(id: number, courseTestQuestionnaire: CourseTestQuestionnaire): Observable<Message> {
    return this.httpClient.put<Message>(`/api/course/v1/course-test-questionnaire/${id}`, courseTestQuestionnaire);
  }

  public deleteCourseTestQuestionnaire(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/course/v1/course-test-questionnaire/${id}`);
  }

  public getQuestionnaireCount(courseId: number, categoryId: number): Observable<number> {
    return this.httpClient.get<number>(`/api/course/v1/course-test-questionnaire/count/${courseId}/${categoryId}`);
  }

  // Course Question APIs
  public createQuestion(createQuestion: Question): Observable<Message> {
    return this.httpClient.post<Message>(`/api/course/v1/course-question`, createQuestion);
  }

  public getQuestions(courseId: number, disabled?: boolean): Observable<Question[]> {
    return this.httpClient.get<Question[]>(`/api/course/v1/course-question`, {
      params: { ...(courseId && { courseId }), ...(disabled !== undefined ? { disabled } : {}) }
    });
  }

  public getQuestion(id: number): Observable<Question> {
    return this.httpClient.get<Question>(`/api/course/v1/course-question/${id}`);
  }

  public updateQuestion(id: number, createQuestion: Question): Observable<Message> {
    return this.httpClient.put<Message>(`/api/course/v1/course-question/${id}`, createQuestion);
  }

  public deleteQuestion(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/course/v1/course-question/${id}`);
  }

  // Employee Test APIs
  public createCourseTestEmployee(courseTestId: number): Observable<Message> {
    return this.httpClient.post<Message>(`/api/course/v1/course-test-employee/${courseTestId}`, {});
  }

  public getCourseTestEmployees(disabled?: boolean): Observable<EmployeeTest[]> {
    return this.httpClient.get<EmployeeTest[]>(`/api/course/v1/course-test-employee`, { params: { ...(disabled != null && { disabled }) } });
  }
  public getCourseTestEmployee(id: number): Observable<EmployeeTest> {
    return this.httpClient.get<EmployeeTest>(`/api/course/v1/course-test-employee/${id}`);
  }

  public updateCourseTestEmployee(id: number, status: string): Observable<Message> {
    return this.httpClient.put<Message>(`/api/course/v1/course-test-employee/${id}?status=${status}`, {});
  }

  // Employee Test Answer APIs
  public createCourseTestEmployeeAnswer(employeeAnswer: EmployeeAnswer): Observable<Message> {
    return this.httpClient.post<Message>(`/api/course/v1/course-test-employee-answer`, employeeAnswer);
  }

  public getCourseTestEmployeeAnswers(disabled?: boolean): Observable<EmployeeAnswer[]> {
    return this.httpClient.get<EmployeeAnswer[]>(`/api/course/v1/course-test-employee-answer`, { params: { ...(disabled != null && { disabled }) } });
  }

  public getCourseTestEmployeeAnswer(id: number): Observable<EmployeeAnswer> {
    return this.httpClient.get<EmployeeAnswer>(`/api/course/v1/course-test-employee-answer/${id}`);
  }

  public updateCourseTestEmployeeAnswer(id: number, employeeAnswer: EmployeeAnswer): Observable<Message> {
    return this.httpClient.put<Message>(`/api/course/v1/course-test-employee-answer/${id}`, employeeAnswer);
  }

  getCourseTestQuestions(courseTestId: number): Observable<Map<string, any[]>> {
    return this.httpClient.get<Map<string, any[]>>(`/api/course/v1/course-test-question/${courseTestId}`);
  }
  // employeeReport
  public getEmployeeTestReport(courseTestEmpId: number): Observable<EmployeeReport> {
    return this.httpClient.get<EmployeeReport>(`/api/course/v1/course-test-employee/report/${courseTestEmpId}`);
  }

  public getEmployeeIsAttend(employeeId: number, courseTestId: number): Observable<boolean> {
    return this.httpClient.get<boolean>(`/api/course/v1/course-test-employee/is-attend/${employeeId}/${courseTestId}`);
  }

  public getIsEmployeeReport(employeeId: number, courseTestId: number): Observable<boolean> {
    return this.httpClient.get<boolean>(`/api/course/v1/course-employee/is-attend/${employeeId}/${courseTestId}`);
  }

  public getEmployeeTestReports(courseTestEmpId: number): Observable<EmployeeReport> {
    return this.httpClient.get<EmployeeReport>(`/api/course/v1/course-test-employee/report`, { params: { ...(courseTestEmpId && { courseTestEmpId }) } });
  }

  public getEmployeeReport(courseTestId : number): Observable<EmployeeReport> {
    return this.httpClient.get<EmployeeReport>(`/api/course/v1/course-test-employee/reports`, { params: { ...(courseTestId  && { courseTestId  }) } });
  }

// Course Report
public getCourseReports( courseTestId): Observable<CourseReport[]> {
    return this.httpClient.get<CourseReport[]>(`/api/course/v1/course-employee/report/${courseTestId}`);

}

//Employee Course Allocation API's
public createEmployeeCourseAllocation(employeeCourseAllocation: EmployeeCourseAllocation): Observable<Message> {
  return this.httpClient.post<Message>(`/api/course/v1/employee-course`, employeeCourseAllocation);
}

public getEmployeeCourseAllocations(employeeId: number): Observable<EmployeeCourseAllocation[]> {
  return this.httpClient.get<EmployeeCourseAllocation[]>(`/api/course/v1/employee-courses`, { params: { ...(employeeId  && { employeeId }) } });
}

public getEmployeeCourseAllocation(id: number): Observable<EmployeeCourseAllocation> {
  return this.httpClient.get<EmployeeCourseAllocation>(`/api/course/v1/employee-course/${id}`);
}

public updateEmployeeCourseAllocation(id: number, employeeCourseAllocation: EmployeeCourseAllocation): Observable<Message> {
  return this.httpClient.put<Message>(`/api/course/v1/employee-course/${id}`, employeeCourseAllocation);
}

public deleteEmployeeCourseAllocation(id: number): Observable<Message> {
  return this.httpClient.delete<Message>(`/api/course/v1/course-question/${id}`);
}
}
